import { v4 as uuidv4 } from 'uuid';
import logger from './logger';

// Create a function to generate request IDs
const generateRequestId = () => uuidv4();

// Setup axios interceptors for automatic logging
export const setupApiLogging = (axiosInstance, options = {}) => {
    if (!axiosInstance) {
        throw new Error('axiosInstance is required for setupApiLogging');
    }
    const { skipLogging = false } = options;
    
    // Skip logging for specific instances (like logging requests themselves)
    if (skipLogging) {
        return axiosInstance;
    }
    
    // Request interceptor
    axiosInstance.interceptors.request.use(
        (config) => {
            // Skip logging for log endpoints to prevent circular logging
            if (config.url?.includes('/logs/')) {
                return config;
            }
            
            const requestId = generateRequestId();
            const startTime = performance.now();
            
            // Attach request metadata
            config.metadata = {
                requestId,
                startTime
            };
            
            // Log the request
            logger.logApiRequest(config, requestId);
            
            return config;
        },
        (error) => {
            logger.error('API Request Setup Failed', {
                error: error.message,
                stack: error.stack
            });
            return Promise.reject(error);
        }
    );

    // Response interceptor
    axiosInstance.interceptors.response.use(
        (response) => {
            // Skip logging for log endpoints to prevent circular logging
            if (response.config.url?.includes('/logs/')) {
                return response;
            }
            
            const endTime = performance.now();
            const duration = Math.round(endTime - response.config.metadata.startTime);
            const requestId = response.config.metadata.requestId;
            
            // Log successful response
            logger.logApiResponse(response, duration, requestId);
            
            return response;
        },
        (error) => {
            // Skip logging for log endpoints to prevent circular logging
            if (error.config?.url?.includes('/logs/')) {
                return Promise.reject(error);
            }
            
            const endTime = performance.now();
            const duration = error.config?.metadata ? 
                Math.round(endTime - error.config.metadata.startTime) : null;
            const requestId = error.config?.metadata?.requestId;
            
            // Log error response
            logger.logApiError(error, duration, requestId);
            
            return Promise.reject(error);
        }
    );
    
    return axiosInstance;
};

export default setupApiLogging; 